import type { CardState } from "@seagames/contracts";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { cardsSummaryOptions } from "../../lib/api/e-accreditation";
import { useDefaultGamesId } from "../../lib/competition/games";
import { CardIcon } from "./icons";
import { Panel, PanelError, Stat, StatGrid, type Tone } from "./sector-parts";

/** The physical card's road, as the E-Accreditation page counts it. */
const STATES: ReadonlyArray<[CardState, Tone]> = [
	["NOT_PRINTED", "y"],
	["PRINTED", "bl"],
	["ACTIVE", "ok"],
	["LOST", "r"],
];

/**
 * Sector 2: the approved cards' physical state (E-Accreditation), one
 * `GET /cards/summary` for the dashboard's games (and organisation, when
 * filtered) — scoped in SQL like every card read. Each number opens the
 * cards list on that state.
 */
export function CardsStrip({ gamesId: picked, orgId }: { gamesId?: string; orgId?: string }) {
	const { t } = useTranslation("dashboard");
	const { t: tE } = useTranslation("eAccreditation");
	const gamesId = useDefaultGamesId(picked);
	const q = useQuery(cardsSummaryOptions(gamesId, orgId));
	const count = (s: CardState) => q.data?.by_state.find((x) => x.state === s)?.count;

	return (
		<div className="mt-[0.875rem]">
			<Panel icon={CardIcon} title={t("sector.cards.title")} sub={t("sector.cards.sub")} to="/accreditation/e-accreditation">
				{q.isError && !q.data ? (
					<PanelError error={q.error} />
				) : (
					<StatGrid>
						<Stat label={t("sector.cards.total")} n={q.data?.total} tone="n" to="/accreditation/e-accreditation/cards" />
						{STATES.map(([s, tone]) => (
							<Stat
								key={s}
								label={tE(`state.${s}`)}
								n={q.data ? (count(s) ?? 0) : undefined}
								tone={tone}
								to="/accreditation/e-accreditation/cards"
								search={{ card_state: s }}
							/>
						))}
					</StatGrid>
				)}
			</Panel>
		</div>
	);
}
